import React, { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import ContentLoader from "../../Pages/LoadingPage/ContentLoader";
import useRelationshipOptions from "../RelationshipForm/useRelationshipOptions";
import { getRelationships, attachInvite, getRequestById, acceptRequestByRequestId, declineRequestByRequestId } from "../../Services/Realtionship";
import "./style.css";

export default function InviteDecisionModal({ open, onClose, invite, onApproved }) {
  // Notification may contain context.request_id; fetch full request details to populate
  const requestId = invite?.context?.request_id || invite?.content?.additional_content?.request_id;
  const [request, setRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [requestError, setRequestError] = useState("");

  // Resolve relation id to act upon: prefer fetched request's relation_id
  const inviteRelationId = (request?.relation_id)
    || invite?.relation_id
    || invite?.invite_relation_id
    || invite?.content?.additional_content?.relation_id
    || invite?.content?.relation_id
    || invite?._id;

  const inviterName = (request?.inviter_profile ? `${request?.inviter_profile?.first_name || ""} ${request?.inviter_profile?.last_name || ""}`.trim() : null)
    || invite?.from_name
    || invite?.content?.title
    || "";
  const inviterType = invite?.relationship_type || invite?.content?.additional_content?.relationship_type || "";

  const [mode, setMode] = useState("new"); // 'new' | 'existing'
  const [form, setForm] = useState({
    relationship_category: "",
    relationship_type: "",
    relationship_status: "",
    relationship_length: "",
    first_name: "",
    last_name: "",
    profile_pic_url: "",
    relationship_goals: "",
  });
  const [relationships, setRelationships] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { categories, types, statuses, loadTypes } = useRelationshipOptions({ initialCategory: form.relationship_category });
  useEffect(() => { if (form.relationship_category) loadTypes(form.relationship_category); }, [form.relationship_category, loadTypes]);

  useEffect(() => {
    if (!open) return;
    setMode("new");
    setForm((p) => ({ ...p, relationship_category: "", relationship_type: "", relationship_status: "", relationship_length: "" }));
    setSelectedExisting("");
    setSearchQuery("");
    setShowTypeahead(false);
    setErrorMsg("");
    setRequest(null);
    setRequestError("");
    if (requestId) {
      (async () => {
        try {
          setLoadingRequest(true);
          const data = await getRequestById(requestId);
          setRequest(data);
        } catch (e) {
          setRequestError(e?.response?.data?.message || e?.message || "Failed to load request");
        } finally {
          setLoadingRequest(false);
        }
      })();
    }
    // prefetch existing list
    (async () => {
      try {
        const data = await getRelationships("");
        setRelationships(Array.isArray(data) ? data : []);
      } catch (_) {
        setRelationships([]);
      }
    })();
  }, [open]);

  const isInvalidRequest = useMemo(() => {
    const s = String(request?.status || "").toLowerCase();
    return !!request && s && s !== "pending";
  }, [request]);

  const filteredRelationships = useMemo(() => {
    const q = String(searchQuery || "").toLowerCase();
    const list = Array.isArray(relationships) ? relationships : [];
    if (!q) return list;
    return list.filter((r) => {
      const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
      const email = (r.email || r.contact?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q) || String(r._id).toLowerCase().includes(q);
    });
  }, [relationships, searchQuery]);

  const requiredNewComplete = useMemo(() => {
    return (
      String(form.relationship_category || "").trim() &&
      String(form.relationship_type || "").trim() &&
      String(form.relationship_status || "").trim()
    );
  }, [form]);

  const handleApprove = async () => {
    // For 'new' we must have requestId per new API
    if (mode === 'new' && !requestId) {
      setErrorMsg("Missing request id");
      return;
    }
    if (mode === 'existing' && !inviteRelationId) {
      setErrorMsg("Missing relation id");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      if (mode === "new") {
        const payload = { ...form };
        await acceptRequestByRequestId(requestId, payload);
      } else {
        if (!selectedExisting) {
          setErrorMsg("Please select a relationship to attach");
          setSubmitting(false);
          return;
        }
        const body = { existing_relation_id: selectedExisting, request_id: requestId };
        await attachInvite(inviteRelationId, body);
      }
      onApproved?.();
      onClose?.();
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e?.message || "Failed to process request";
      setErrorMsg(apiMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!requestId) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await declineRequestByRequestId(requestId);
      onApproved?.();
      onClose?.();
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e?.message || "Failed to decline";
      setErrorMsg(apiMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="invite-overlay" onClick={onClose}>
      <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invite-header-row">
          <h3>Connect with</h3>
          <button className="icon-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="share-contact-card">
          <div className="avatar-round">
            {request?.inviter_profile?.profile_pic_url ? (
              <img
                src={request.inviter_profile.profile_pic_url}
                alt={inviterName || "Inviter"}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              (inviterName || "").charAt(0).toUpperCase()
            )}
          </div>
          <div className="share-contact-info">
            <div className="name">{inviterName}</div>
            <div className="sub">{inviterType}</div>
            {loadingRequest ? (<div className="sub">Loading request…</div>) : null}
            {requestError ? (<div className="invite-error">{requestError}</div>) : null}
          </div>
        </div>

        {loadingRequest ? (
          <div className="invite-loader"><ContentLoader /></div>
        ) : isInvalidRequest ? (
          <div className="invite-error-banner">
            <AlertTriangle size={18} />
            <span>This request is no longer valid. It may have expired or been canceled.</span>
          </div>
        ) : (
          <>
            <div className="invite-body">
              <div className="invite-mode">
                <label><input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> New Relationship</label>
                <label><input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} /> Existing Relationship</label>
              </div>

              {mode === 'new' ? (
                <div className="grid-2">
                  <div className="field">
                    <label>Relationship Category</label>
                    <select value={form.relationship_category} onChange={(e) => setForm((p) => ({ ...p, relationship_category: e.target.value, relationship_type: '' }))}>
                      <option value="">Select Category</option>
                      {Array.isArray(categories) && categories.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Relationship Type</label>
                    <select value={form.relationship_type} onChange={(e) => setForm((p) => ({ ...p, relationship_type: e.target.value }))}>
                      <option value="">Select Type</option>
                      {Array.isArray(types) && types.map((t) => (<option key={t} value={t}>{t}</option>))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Relationship Status</label>
                    <select value={form.relationship_status} onChange={(e) => setForm((p) => ({ ...p, relationship_status: e.target.value }))}>
                      <option value="">Select Status</option>
                      {Array.isArray(statuses) && statuses.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="field typeahead">
                  <label>Select Existing Relationship</label>
                  <input
                    className="typeahead-input"
                    placeholder="Search relationships"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowTypeahead(true)}
                  />
                  {showTypeahead && (
                    <div className="typeahead-list" onMouseLeave={() => setShowTypeahead(false)}>
                      {filteredRelationships.length === 0 ? (
                        <div className="typeahead-empty">No matches</div>
                      ) : (
                        filteredRelationships.map((r) => {
                          const label = `${r.first_name || ''} ${r.last_name || ''}`.trim() || r._id;
                          return (
                            <div
                              key={r._id}
                              className={`typeahead-item ${selectedExisting === r._id ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedExisting(r._id);
                                setSearchQuery(label);
                                setShowTypeahead(false);
                              }}
                            >
                              {label}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {errorMsg ? (<div className="invite-error" role="alert" aria-live="assertive">{errorMsg}</div>) : null}

            <div className="invite-actions">
              <button className="primary" disabled={submitting || (mode==='new' && !requiredNewComplete)} onClick={handleApprove}>Approve</button>
              <button className="ghost" disabled={submitting} onClick={handleDecline}>Decline</button>
            </div>
          </>
        )}
        {submitting && (
          <div className="invite-submit-overlay" aria-live="polite" aria-busy="true">
            <ContentLoader />
          </div>
        )}
      </div>
    </div>
  );
}
